import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import Loader from '../../../../component/common/Loader';
import Topbar from '../../../PulseSurvey/Topbar';
import { CompanyApi } from '../../../../api/companyApi';
import { errorToast } from '../../../../utils/helper';
import ProfilePic from '../../../../assets/images/dummy_profile.png';

function CBadgeProgress() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const badgeId = searchParams.get('badgeId');

  const [loader, setLoader] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchProgress = async () => {
      if (!userId || !badgeId) {
        errorToast('Missing user or badge reference');
        return;
      }
      setLoader(true);
      try {
        const response = await CompanyApi.getUserBadgeProgress(userId, badgeId);
        if (response?.data?.success === true) {
          setData(response.data.data || null);
        } else {
          setData(null);
          errorToast(response?.data?.message || 'Unable to load badge progress');
        }
      } catch (error) {
        setData(null);
        errorToast('An unexpected error occurred.');
        console.error('CBadgeProgress fetch error:', error);
      } finally {
        setLoader(false);
      }
    };
    fetchProgress();
  }, [userId, badgeId]);

  const user = data?.user;
  const badge = data?.badge;
  const tasks = data?.tasks || [];
  const summary = data?.summary;

  const progressLabel = (progress) => {
    if (!progress) return '';
    return progress.daily
      ? `${progress.current} / ${progress.target} days`
      : `${progress.current} / ${progress.target}`;
  };

  const progressPercent = (progress) => {
    if (!progress || !progress.target) return 0;
    return Math.min(Math.round((progress.current / progress.target) * 100), 100);
  };

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>B2B | Badge Progress</title>
      </Helmet>
      <Topbar title={'Badges'} />
      {loader ? <Loader /> : null}

      <div className="flex items-center gap-2 mt-6">
        <button type="button" onClick={() => navigate(-1)} className="flex items-center justify-center">
          <ChevronLeftIcon className="w-7 h-7 text-shoorah-lightBlack dark:text-white" />
        </button>
        <h1 className="font-medium leading-9 text-shoorah-lightBlack dark:text-white text-2xl">
          Badge Progress
        </h1>
      </div>

      {!loader && !data ? (
        <div className="mt-10 text-center text-gray-500 dark:text-shoorah-darkBg">
          No progress data available.
        </div>
      ) : null}

      {data && (
        <>
          {/* Badge + user header */}
          <div className="bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray rounded-2xl p-5 mt-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img
                src={badge?.image || ProfilePic}
                onError={(e) => {
                  e.target.src = ProfilePic;
                }}
                alt="badge"
                className="w-20 h-20 rounded-full object-cover"
              />
              <div>
                <h2 className="text-xl font-semibold text-shoorah-lightBlack dark:text-white">
                  {badge?.title}
                </h2>
                <p className="text-sm text-gray-500 dark:text-shoorah-darkBg mt-1">
                  {badge?.wellpoints ? `${badge.wellpoints} Wellpoints` : null}
                </p>
                {summary && (
                  <span
                    className={`inline-block mt-2 px-3 py-1 text-xs rounded-full border ${
                      summary.earned
                        ? 'bg-[#DDF2BF] border-[#438038] text-[#438038]'
                        : 'bg-[#F9F2F5] border-[#BF3261] text-[#BF3261]'
                    }`}
                  >
                    {summary.earned ? 'Earned' : 'In progress'}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 md:border-l md:pl-5 border-shoorah-borderGray">
              <img
                src={user?.user_profile || ProfilePic}
                onError={(e) => {
                  e.target.src = ProfilePic;
                }}
                alt="user"
                className="w-12 h-12 rounded-full object-cover"
              />
              <div>
                <div className="font-medium text-shoorah-lightBlack dark:text-white">{user?.name}</div>
                <div className="text-sm text-gray-500 lowercase">{user?.email}</div>
                {summary && (
                  <div className="text-xs text-gray-500 dark:text-shoorah-darkBg mt-1">
                    {summary.completedTasks} / {summary.totalTasks} tasks completed
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tasks */}
          <h3 className="font-medium text-lg text-shoorah-lightBlack dark:text-white mt-6 mb-3">Tasks</h3>
          <div className="flex flex-col gap-4">
            {tasks.length === 0 && (
              <div className="text-gray-500 dark:text-shoorah-darkBg">This badge has no tasks.</div>
            )}
            {tasks.map((task) => {
              const percent = progressPercent(task.progress);
              return (
                <div
                  key={task._id}
                  className="bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray rounded-2xl p-5"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-shoorah-lightBlack dark:text-white">
                          {task.title}
                        </h4>
                        {task.feature && (
                          <span className="text-xs px-2 py-[2px] rounded-full bg-[#F1F1F1] text-[#0B0B0B] capitalize">
                            {String(task.feature).replace(/_/g, ' ').toLowerCase()}
                          </span>
                        )}
                      </div>
                      {task.description && (
                        <p className="text-sm text-gray-500 dark:text-shoorah-darkBg mt-1">
                          {task.description}
                        </p>
                      )}
                    </div>
                    <span
                      className={`flex-shrink-0 px-3 py-1 text-xs rounded-full border ${
                        task.progress?.completed
                          ? 'bg-[#DDF2BF] border-[#438038] text-[#438038]'
                          : 'bg-[#F4F4F4] border-[#9CA3AF] text-[#6B7280]'
                      }`}
                    >
                      {task.progress?.completed ? 'Completed' : 'Pending'}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-shoorah-darkBg mb-1">
                      <span>Progress</span>
                      <span>{progressLabel(task.progress)}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-shoorah-darkBgColor rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full ${
                          task.progress?.completed ? 'bg-[#438038]' : 'bg-shoorah-active'
                        }`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default CBadgeProgress;
